import React, { useEffect } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useInviteExistingUserToOrganization,
  useUpdateOrganization,
} from "@via/pages/organization/organization.api";
import { OnboardingStep } from "common";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useCreateSetupIntent } from "@via/pages/billing/billing.api";

import LoginLink from "@via/components/login-link.tsx";

interface ConfirmationScreenProps {
  onUserUpgradeEnd?: () => void;
  enablePayment?: boolean;
}

const ConfirmationScreen: React.FC<ConfirmationScreenProps> = ({
  onUserUpgradeEnd,
  enablePayment = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setShowOrganizationCreateModal } = useGlobal();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { mutate: inviteExistingUserToOrganization } =
    useInviteExistingUserToOrganization();

  const {
    mutate: createSetupIntent,
    data: setupIntentData,
    isPending: isSetupIntentPending,
  } = useCreateSetupIntent();

  const {
    mutateAsync: updateOrganization,
    isPending: isUpdateOrganizationPending,
  } = useUpdateOrganization();

  useEffect(() => {
    // Navigate directly to activity if org already created
    const organizationId = user?.userOrganizations?.[0]?.id;
    if (
      user?.onboardingStep === OnboardingStep.OrganizationCreated &&
      organizationId
    ) {
      navigate({
        pathname: `/organization/${organizationId}`,
      });
    }
  }, [user?.onboardingStep]);

  useEffect(() => {
    // If Stripe is disabled and organization is not created yet, create it.
    if (
      !enablePayment &&
      (!user?.userOrganizations || user?.userOrganizations.length === 0) &&
      !isSetupIntentPending &&
      !setupIntentData
    ) {
      createSetupIntent();
    }
  }, [
    enablePayment,
    user?.userOrganizations,
    createSetupIntent,
    isSetupIntentPending,
    setupIntentData,
  ]);

  const handleCreateOrganizationClick = async () => {
    const organizationId = user?.userOrganizations?.[0]?.id;
    let organizationName = user?.userOrganizations?.[0]?.name;
    const showCreateModal = user?.registrationType === "self";

    if (!organizationName) {
      organizationName = `${user?.firstName ?? "My"} Organization`;
    }

    await updateOrganization({
      requestParams: {
        id: organizationId,
        onboardingStep: user?.onboardingStep,
      },
      requestBody: { name: organizationName },
    });
    setShowOrganizationCreateModal(showCreateModal);
    if (onUserUpgradeEnd) {
      onUserUpgradeEnd();
    }
    navigate({
      pathname: `/organization/${organizationId}/activity`,
    });
  };

  const handleJoinOrganizationClick = () => {
    inviteExistingUserToOrganization({
      token: token || "",
    });
  };

  return (
    <div className="flex flex-col gap-5 justify-center items-center p-8">
      <h2 className="text-2xl font-bold">
        {token ? "User Created Successfully!" : "Subscription Completed!"}
      </h2>
      <CheckCircleIcon className="h-35 w-35 text-green-600" />
      <div className="max-w-screen-md mx-auto flex justify-center gap-5 px-4 py-5">
        {token ? (
          <button
            onClick={handleJoinOrganizationClick}
            className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500">
            Join your organization
          </button>
        ) : (
          <button
            onClick={handleCreateOrganizationClick}
            disabled={isUpdateOrganizationPending}
            className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500">
            <Spinner loading={isUpdateOrganizationPending}>
              Create your organization
            </Spinner>
          </button>
        )}
      </div>
      <LoginLink className="mt-0" logoutBeforeLogin={true} />
    </div>
  );
};

export default ConfirmationScreen;
